import {
  assertAcceptableKdf,
  computeLoginRootKey,
  deriveLoginKeysFromRoot,
  deriveRecoveryProof,
  kdfSaltBytes,
  kdfToArgon2,
  type AccountKdfParams,
  type DeriveLoginKeysOptions,
  type LoginKeys,
} from './account-keys';
import { computeMasterKey, deriveKeysFromMaster, type DerivedKeys, type DeriveKeysOptions } from './keys';
import { normalizePassphrase } from './passphrase';
import type {
  LoginRootRequest,
  LoginRootResponse,
  MasterKeyRequest,
  MasterKeyResponse,
} from './worker-protocol';

/** The part of the Web Worker interface used here (so tests can supply a fake). */
export interface KdfWorker<
  Request extends { readonly id: number } = MasterKeyRequest,
  Response extends { readonly id: number } = MasterKeyResponse,
> {
  postMessage(message: Request): void;
  addEventListener(type: 'message', listener: (event: { data: Response }) => void): void;
  addEventListener(type: 'error', listener: (event: unknown) => void): void;
  terminate(): void;
}

export interface DeriveInWorkerOptions extends DeriveKeysOptions {
  /** Creates the worker; defaults to the bundled Argon2 worker when `Worker` exists. */
  readonly createWorker?: () => KdfWorker;
}

export interface DeriveLoginInWorkerOptions extends DeriveLoginKeysOptions {
  /** Creates the worker; defaults to the bundled Argon2 worker when `Worker` exists. */
  readonly createWorker?: () => KdfWorker<LoginRootRequest, LoginRootResponse>;
}

/** The sync keys of a recovery code together with its recovery proof. */
export interface DerivedKeysWithRecoveryProof {
  readonly keys: DerivedKeys;
  /** base64url of 32 bytes; sent only on registration, upgrade and password reset. */
  readonly recoveryProof: string;
}

let nextRequestId = 1;

function defaultWorker<Request extends { readonly id: number }, Response extends { readonly id: number }>():
  KdfWorker<Request, Response> | null {
  if (typeof Worker === 'undefined') return null;
  try {
    return new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' }) as unknown as KdfWorker<
      Request,
      Response
    >;
  } catch {
    return null;
  }
}

/** Run the Argon2 step in `worker`; rejects when the worker fails (the caller then falls back). */
function masterKeyInWorker(
  worker: KdfWorker,
  passphrase: string,
  options: DeriveKeysOptions,
): Promise<Uint8Array> {
  return new Promise<Uint8Array>((resolve, reject) => {
    const id = nextRequestId++;
    worker.addEventListener('message', (event) => {
      const response = event.data;
      if (response.id !== id) return;
      if ('master' in response) resolve(response.master);
      else reject(new Error(response.error));
    });
    worker.addEventListener('error', () => {
      reject(new Error('The key derivation worker failed'));
    });
    worker.postMessage({
      id,
      passphrase,
      ...(options.argon2 === undefined ? {} : { argon2: options.argon2 }),
    });
  });
}

/**
 * The master key of `passphrase`: Argon2 in a Web Worker, or on the calling thread when no worker can be
 * created or the worker fails. The caller owns the result and clears it.
 */
async function masterKey(passphrase: string, options: DeriveInWorkerOptions): Promise<Uint8Array> {
  const worker = (options.createWorker ?? (() => defaultWorker<MasterKeyRequest, MasterKeyResponse>()))();
  let master: Uint8Array | null = null;
  if (worker !== null) {
    try {
      master = await masterKeyInWorker(worker, passphrase, options);
    } catch {
      master = null;
    } finally {
      worker.terminate();
    }
  }
  return master ?? (await computeMasterKey(normalizePassphrase(passphrase), options.argon2));
}

/**
 * Derive the keys with Argon2 in a Web Worker (HKDF runs here afterwards). Falls back to the
 * calling thread when no worker can be created or the worker fails, so this works in Node,
 * in tests and in browsers that block module workers.
 */
export async function deriveKeysInWorker(
  passphrase: string,
  options: DeriveInWorkerOptions = {},
): Promise<DerivedKeys> {
  const master = await masterKey(passphrase, options);
  try {
    return await deriveKeysFromMaster(master);
  } finally {
    master.fill(0);
  }
}

/** Like `deriveKeysInWorker`, and also the recovery proof, from one Argon2 run. */
export async function deriveKeysWithRecoveryProofInWorker(
  passphrase: string,
  options: DeriveInWorkerOptions = {},
): Promise<DerivedKeysWithRecoveryProof> {
  const master = await masterKey(passphrase, options);
  try {
    const [keys, recoveryProof] = await Promise.all([deriveKeysFromMaster(master), deriveRecoveryProof(master)]);
    return { keys, recoveryProof };
  } finally {
    master.fill(0);
  }
}

function loginRootInWorker(
  worker: KdfWorker<LoginRootRequest, LoginRootResponse>,
  request: Omit<LoginRootRequest, 'id' | 'kind'>,
): Promise<Uint8Array> {
  return new Promise<Uint8Array>((resolve, reject) => {
    const id = nextRequestId++;
    worker.addEventListener('message', (event) => {
      const response = event.data;
      if (response.id !== id) return;
      if ('root' in response) resolve(response.root);
      else reject(new Error(response.error));
    });
    worker.addEventListener('error', () => {
      reject(new Error('The key derivation worker failed'));
    });
    worker.postMessage({ id, kind: 'loginRoot', ...request });
  });
}

/**
 * Derive the login proof and wrap key with Argon2 in a Web Worker; same fallback as `deriveKeysInWorker`.
 * Unsafe server parameters are rejected before anything is sent to the worker.
 */
export async function deriveLoginKeysInWorker(
  password: string,
  params: AccountKdfParams,
  options: DeriveLoginInWorkerOptions = {},
): Promise<LoginKeys> {
  assertAcceptableKdf(params);
  const salt = kdfSaltBytes(params);
  const argon2 = options.argon2Override ?? kdfToArgon2(params);
  const worker = (options.createWorker ?? (() => defaultWorker<LoginRootRequest, LoginRootResponse>()))();
  let root: Uint8Array | null = null;
  if (worker !== null) {
    try {
      root = await loginRootInWorker(worker, { password, salt, argon2 });
    } catch {
      root = null;
    } finally {
      worker.terminate();
    }
  }
  root ??= await computeLoginRootKey(password, salt, argon2);
  try {
    return await deriveLoginKeysFromRoot(root);
  } finally {
    root.fill(0);
  }
}
